// The Sting Effect (Precise, Lethal, Magical Dagger Strike)
// Features: 
// 1. Dagger materializes out of glowing blue energy above caster
// 2. Aims and charges with piercing particle trails
// 3. Lightning-fast thrust to target
// 4. Shattering blue stinger impact, leaving frost-like sparks
// 5. Fades into mist

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 1.2, // Doubled from 0.6
    materializeDuration: 500,
    aimDuration: 600,
    strikeDuration: 150, // Extremely fast
    impactDuration: 400,
    vanishDuration: 500,
    paths: {
        sting: '/assets/effects/the_sting/sting.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.sting);

// ==========================================
// PARTICLES
// ==========================================

class ChargingMote {
    x: number; y: number;
    startX: number; startY: number;
    delay: number; duration: number;
    lifeTime: number = 0;
    size: number;
    color: string;

    constructor(targetX: number, targetY: number) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 100 + Math.random() * 200;
        this.startX = targetX + Math.cos(angle) * dist;
        this.startY = targetY + Math.sin(angle) * dist;
        this.x = this.startX;
        this.y = this.startY;

        this.delay = Math.random() * 200;
        this.duration = 200 + Math.random() * 300;
        this.size = 1 + Math.random() * 3;

        const colors = ['#38bdf8', '#bae6fd', '#0284c7', '#ffffff'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number, targetX: number, targetY: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            return true;
        }
        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress >= 1) progress = 1;

        const ease = Math.pow(progress, 3); // accelerate deeply

        this.x = this.startX + (targetX - this.startX) * ease;
        this.y = this.startY + (targetY - this.startY) * ease;

        return progress < 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        // Elongated along motion path
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 5;
        ctx.fill();
        ctx.restore();
    }
}

class StingSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;
    rotation: number;
    rotSpeed: number;

    constructor(startX: number, startY: number, strikeAngle: number) {
        this.x = startX;
        this.y = startY;

        // Spray sharply along the strike path
        const speed = 10 + Math.random() * 25;
        const spread = (Math.random() - 0.5) * Math.PI / 2;
        const angle = strikeAngle + spread;

        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.life = 1.0;
        this.size = 2 + Math.random() * 4;

        const colors = ['#e0f2fe', '#7dd3fc', '#0ea5e9'];
        this.color = colors[Math.floor(Math.random() * colors.length)];

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.5;
    }

    update(dt: number) {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.90; // High friction, air resistance for needle spray
        this.vy *= 0.90;
        this.life -= 0.03;
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.strokeStyle = this.color;
        ctx.lineWidth = 2;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 10 * this.life;

        // Draw as sharp needles
        ctx.beginPath();
        ctx.moveTo(-this.size * 2, 0);
        ctx.lineTo(this.size * 2, 0);
        ctx.stroke();

        ctx.restore();
    }
}

export type TheStingEffect = {
    kind: 'theSting'
    t: number
    sx: number; sy: number; // Raw Caster Center
    tx: number; ty: number; // Target
    isOpponent: boolean;
    data?: {
        phase: 'MATERIALIZE' | 'AIM' | 'STRIKE' | 'IMPACT' | 'VANISH'
        motes: ChargingMote[]
        sparks: StingSpark[]
        attackAngle: number
        hoverX: number
        hoverY: number
    }
}

export const createTheStingEffect = (sx: number, sy: number, tx: number, ty: number, isOpponent: boolean): TheStingEffect => ({
    kind: 'theSting', t: 0, sx, sy, tx, ty, isOpponent
});

export const renderTheStingEffect = (
    eff: TheStingEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        // Adjust spawn point based on whether the caster is the opponent
        // Opponent is at top (y ~100), Player is at bottom
        // We want it to spawn *in front* of the avatar.
        // For opponent: spawn slightly below them (+Y)
        // For player: spawn slightly above them (-Y)
        const spawnYOffset = eff.isOpponent ? 150 : -150;

        const actualSx = eff.sx;
        const actualSy = eff.sy + spawnYOffset;

        const dx = eff.tx - actualSx;
        const dy = eff.ty - actualSy;
        const attackAngle = Math.atan2(dy, dx);

        // Hover slightly back from the actual spawn point
        const hoverX = actualSx - Math.cos(attackAngle) * 50;
        const hoverY = actualSy - Math.sin(attackAngle) * 50;

        const motes: ChargingMote[] = [];
        for (let i = 0; i < 40; i++) {
            motes.push(new ChargingMote(hoverX, hoverY));
        }

        eff.data = {
            phase: 'MATERIALIZE',
            motes,
            sparks: [],
            attackAngle,
            hoverX,
            hoverY
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tMat = CONFIG.materializeDuration;
    const tAim = tMat + CONFIG.aimDuration;
    const tStrike = tAim + CONFIG.strikeDuration;
    const tImpact = tStrike + CONFIG.impactDuration;
    const tTotal = tImpact + CONFIG.vanishDuration;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tAim) d.phase = 'AIM';
    else if (eff.t < tStrike) d.phase = 'STRIKE';
    else if (eff.t < tImpact) d.phase = 'IMPACT';
    else d.phase = 'VANISH';

    ctx.save();

    let daggerAlpha = 0;
    let daggerScale = CONFIG.scale;
    let currentX = eff.sx;
    let currentY = eff.sy;
    // Base rotation assuming SVG points DOWN (PI / 2)
    let daggerRotation = d.attackAngle - Math.PI / 2;
    let motionBlur = 0;

    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        daggerAlpha = p; // Fade in
        const easeOut = 1 - Math.pow(1 - p, 3);

        const spawnYOffset = eff.isOpponent ? 150 : -150;
        const actualSx = eff.sx;
        const actualSy = eff.sy + spawnYOffset;

        // Rise up to hover position from slightly further back/down
        const riseFromYOffset = eff.isOpponent ? -50 : 50; // Come from behind avatar
        currentX = actualSx + (d.hoverX - actualSx) * easeOut;
        currentY = actualSy + riseFromYOffset * (1 - easeOut) + (d.hoverY - actualSy) * easeOut;

        // Start pointing upwards/downwards based on owner, then snap to aim at target
        const startRot = eff.isOpponent ? Math.PI / 2 : -Math.PI / 2;
        daggerRotation = startRot + (d.attackAngle - Math.PI / 2 - startRot) * easeOut;

        // Draw blue mist rings at spawn
        ctx.save();
        ctx.globalAlpha = (1 - p) * 0.5;
        ctx.translate(currentX, currentY);
        ctx.beginPath();
        ctx.arc(0, 0, p * 150, 0, Math.PI * 2);
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 10 * (1 - p);
        ctx.stroke();
        ctx.restore();

    } else if (d.phase === 'AIM') {
        daggerAlpha = 1;
        const p = (eff.t - tMat) / CONFIG.aimDuration;

        // Pull back slightly like a spring
        const pullback = Math.pow(p, 2) * 50;
        currentX = d.hoverX - Math.cos(d.attackAngle) * pullback;
        currentY = d.hoverY - Math.sin(d.attackAngle) * pullback;

        // Render gathering stinger particles
        // Note: The tip of the dagger is mathematically offset.
        // Assuming SVG height 1000, rotated around center (300, 500). Tip is at (300, 1000) roughly.
        // Rotation anchor is center, tip offset is +450 pixels strictly down in untransformed space.

        const tipDistance = 450 * CONFIG.scale;
        const tipX = currentX + Math.cos(d.attackAngle) * tipDistance;
        const tipY = currentY + Math.sin(d.attackAngle) * tipDistance;

        d.motes = d.motes.filter(m => m.update(dt, tipX, tipY));
        d.motes.forEach(m => m.draw(ctx));

    } else if (d.phase === 'STRIKE') {
        daggerAlpha = 1;
        const p = (eff.t - tAim) / CONFIG.strikeDuration;
        const easeIn = Math.pow(p, 3);

        // The pullback ending spot
        const startX = d.hoverX - Math.cos(d.attackAngle) * 50;
        const startY = d.hoverY - Math.sin(d.attackAngle) * 50;

        currentX = startX + (eff.tx - startX) * easeIn;
        currentY = startY + (eff.ty - startY) * easeIn;

        motionBlur = 40;

        // Contrailect drawing (Blue Streak)
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(currentX, currentY);
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
        ctx.lineWidth = 60 * CONFIG.scale;
        ctx.lineCap = 'round';
        ctx.stroke();
        // Inner white streak
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(currentX, currentY);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 20 * CONFIG.scale;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();

    } else if (d.phase === 'IMPACT') {
        daggerAlpha = 1;
        currentX = eff.tx;
        currentY = eff.ty;

        // Spawn sharp impact sparks
        if (d.sparks.length === 0) {
            for (let i = 0; i < 50; i++) {
                d.sparks.push(new StingSpark(eff.tx, eff.ty, d.attackAngle));
            }
            // Screen shake
            ctx.translate((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);

            // Impact flash
            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.translate(eff.tx, eff.ty);
            ctx.beginPath();
            ctx.arc(0, 0, 150, 0, Math.PI * 2);
            const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 150);
            grad.addColorStop(0, '#ffffff');
            grad.addColorStop(0.3, '#38bdf8');
            grad.addColorStop(1, 'rgba(2, 132, 199, 0)');
            ctx.fillStyle = grad;
            ctx.fill();
            ctx.restore();
        }

    } else if (d.phase === 'VANISH') {
        const p = (eff.t - tImpact) / CONFIG.vanishDuration;
        daggerAlpha = 1 - p;
        if (daggerAlpha < 0) daggerAlpha = 0;

        currentX = eff.tx;
        currentY = eff.ty;

        // Slowly float up as it fades
        currentY -= p * 30;
    }

    // Update and draw sparks
    if (d.phase === 'IMPACT' || d.phase === 'VANISH') {
        d.sparks.forEach(s => {
            s.update(dt);
            s.draw(ctx);
        });
    }

    // Draw the Dagger
    if (daggerAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = daggerAlpha;
        ctx.translate(currentX, currentY);
        ctx.rotate(daggerRotation);

        // Motion stretch during strike
        if (d.phase === 'STRIKE') {
            ctx.scale(CONFIG.scale, CONFIG.scale * 2.5); // Stretch vertically along orientation
        } else {
            ctx.scale(CONFIG.scale, CONFIG.scale);
        }

        const img = getSprite(CONFIG.paths.sting);
        if (img.complete && img.naturalWidth > 0) {
            ctx.save();

            // Strong drop shadow
            ctx.shadowColor = 'rgba(0,0,0,0.8)';
            ctx.shadowBlur = 10;
            ctx.shadowOffsetY = 10;

            if (motionBlur > 0) {
                ctx.shadowColor = '#38bdf8';
                ctx.shadowBlur = motionBlur;
                ctx.shadowOffsetY = 0;
                ctx.globalCompositeOperation = 'screen';
            }

            // The image's native center is roughly (300, 500) of its native 600x1000 size
            // Tip points down.
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);

            ctx.restore();
        }
        ctx.restore();
    }

    ctx.restore();

    return eff.t < tTotal;
};
